import { formatDate, Heading, Section, stringToRichText } from "src/utils"
import React from "react"
import { Container } from "src/layout/container/Container"
import cn from "classnames"
import { InternalLink, PageThemeType } from "../../sanity/types"
import { LinkHandler } from "../../components/link-handler/LinkHandler"
import Media from "src/components/media/Media"
import s from "./BlogPostGridModule.module.scss"
import { TeaserBlogPost } from "src/templates/blog/blogHelper"
import CollageModule, { BoxProps } from "../collage-module/CollageModule"
import { useSettings } from "src/layout/page/settingsContext"
import { getTranslation } from "src/lib/translations"

interface BlogPostGridModuleProps {
    title?: string
    blogPosts: TeaserBlogPost[]
    columns: number
    showFirstBlogPostsInFeatureDesign?: boolean
    pageThemeType?: PageThemeType
    link?: InternalLink
    showOnlyCategory?: string
    blogPostSource?: string
}

function Wrapper({
    link,
    children,
    pageThemeType,
}: {
    link: InternalLink
    children: React.ReactNode
    pageThemeType?: PageThemeType
}) {
    if (!link) {
        return <>{children}</>
    }

    return (
        <LinkHandler
            link={link}
            className={cn(
                s["blog-grid-module__item__link"],
                pageThemeType === "dark" ? s["blog-grid-module__item__link--dark-theme"] : "",
            )}
            prefetch={false}
        >
            {children}
        </LinkHandler>
    )
}

export default function BlogPostGridModule({
    title,
    blogPosts,
    link,
    columns,
    showFirstBlogPostsInFeatureDesign,
    pageThemeType,
    showOnlyCategory,
    blogPostSource,
}: BlogPostGridModuleProps): JSX.Element {
    const { lang } = useSettings()
    const t = getTranslation("blog", lang)

    const isAllBlogPostsFromSameCategory = (blogPosts || []).every(
        (b) => b.category.slug === blogPosts[0].category.slug,
    )

    function trimTextWithElipsis(str: string, blogpost, length = 140) {
        if (typeof str !== "string") return null

        if (!str || str.length <= length) return str
        const sliced = str.substr(0, length)
        return sliced.substr(0, Math.min(sliced.length, sliced.lastIndexOf(" "))) + "..."
    }

    function blogPostToCollageBox(blogPost: TeaserBlogPost): BoxProps {
        if (!blogPost) return null
        return {
            subtitle: {
                text: blogPost.category.title + " - " + formatDate(new Date(blogPost.publishedAt), blogPost.lang),
            },
            title: {
                text: stringToRichText(blogPost.title),
                size: "heading-200",
            },
            cta: {
                link: {
                    ...blogPost.link,
                    title: t.readMore,
                },
                style: "button",
            },
        }
    }

    return (
        <Section>
            {showFirstBlogPostsInFeatureDesign && blogPosts.length > 0 && (
                <>
                    <CollageModule
                        boxFormat="medium"
                        box1={blogPostToCollageBox(blogPosts[0])}
                        box2={{}}
                        backgroundMedia={{ media: blogPosts?.[0]?.mainImage }}
                        boxedContainer={true}
                        backgroundColor="black"
                    />
                    {blogPosts.length > 1 && (
                        <CollageModule
                            boxFormat="small"
                            box1={{
                                ...blogPostToCollageBox(blogPosts[1]),
                                boxSettings: { boxed: true, backgroundColor: "black" },
                                media: { media: blogPosts?.[1]?.mainImage, position: "background-fill" },
                            }}
                            box2={
                                !blogPosts[2]
                                    ? {}
                                    : {
                                          ...blogPostToCollageBox(blogPosts[2]),
                                          boxSettings: { boxed: true, backgroundColor: "black" },
                                          media: { media: blogPosts?.[2]?.mainImage, position: "background-fill" },
                                      }
                            }
                        />
                    )}
                </>
            )}
            <Container>
                <div className={cn(s["blog-grid-module"], s["blog-grid-module--columns-" + columns])}>
                    {title && <Heading className={cn(s["blog-grid-module__title"], "heading-300")}>{title}</Heading>}

                    <ul className={cn(s["blog-grid-module__list"], s["blog-grid-module__list--columns-" + columns])}>
                        {(blogPosts || [])
                            // If showing first blog posts in feature design, only show the first 3
                            .slice(showFirstBlogPostsInFeatureDesign ? 3 : 0)
                            // If filtering by category
                            .filter((b) => !showOnlyCategory || b?.category?.slug === showOnlyCategory)
                            // If this component is made from a module in Sanity, and is populated by categories, only show 2 rows
                            .slice(0, blogPostSource === "categories" ? columns * 2 : blogPosts?.length)
                            .map((blogPost) => {
                                return (
                                    <li
                                        key={blogPost.id}
                                        className={cn(
                                            s["blog-grid-module__item"],
                                            s["blog-grid-module__item--columns-" + columns],
                                        )}
                                    >
                                        <Wrapper link={blogPost.link} pageThemeType={pageThemeType}>
                                            <Section>
                                                {blogPost.mainImage?.src && (
                                                    <div className={cn(s["blog-grid-module__item__image"])}>
                                                        <Media
                                                            media={blogPost.mainImage}
                                                            sizes={["(max-width: 1400px) 33vw", "420px"]}
                                                            {...blogPost.mainImage}
                                                            width={840}
                                                            height={0.75 * 840}
                                                            step={200}
                                                        />
                                                    </div>
                                                )}

                                                <div className={cn("paragraph-100", s["blog-grid-module__meta"])}>
                                                    {!isAllBlogPostsFromSameCategory && (
                                                        <>
                                                            <span className={cn(s["blog-grid-module__meta__category"])}>
                                                                {blogPost.category.title}
                                                            </span>{" "}
                                                            -{" "}
                                                        </>
                                                    )}
                                                    <time dateTime={blogPost.publishedAt}>
                                                        {formatDate(new Date(blogPost.publishedAt), blogPost.lang)}
                                                    </time>
                                                </div>

                                                <Heading
                                                    className={cn(s["blog-grid-module__item__title"], "heading-50")}
                                                >
                                                    {blogPost.title}
                                                </Heading>

                                                <p className={cn(s["blog-grid-module__body"], "paragraph-200")}>
                                                    {trimTextWithElipsis(blogPost.intro, blogPost)}
                                                </p>
                                            </Section>
                                        </Wrapper>
                                    </li>
                                )
                            })}
                    </ul>

                    {!!link && (
                        <div className={s["blog-grid-module__cta"]}>
                            <LinkHandler link={link} prefetch={false} />
                        </div>
                    )}
                </div>
            </Container>
        </Section>
    )
}
